import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ModuleAccessGuard } from '@/components/ModuleAccessGuard';
import { PDFPreview } from '@/components/shared/PDFPreview';
import { ConfidenceBadge } from '@/components/shared/ConfidenceBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useBillDetails } from '@/hooks/useBillsAPI';
import { useUpdateParsedFields, useApproveBill, useRejectBill } from '@/hooks/useParserData';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';

const BillDetails = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();

  const { data: bill, isLoading } = useBillDetails(billId!);
  const updateFields = useUpdateParsedFields();
  const approveBill = useApproveBill();
  const rejectBill = useRejectBill();

  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_date: '',
    amount_due: '',
    due_date: '',
    billing_period_from: '',
    billing_period_to: '',
    usage_amount: '',
    usage_unit: '',
  });

  useEffect(() => {
    if (bill?.parsed_data) {
      setFormData({
        invoice_number: bill.parsed_data.invoice_number || '',
        invoice_date: bill.parsed_data.invoice_date || '',
        amount_due: bill.parsed_data.amount_due?.toString() || '',
        due_date: bill.parsed_data.due_date || '',
        billing_period_from: bill.parsed_data.billing_period_from || '',
        billing_period_to: bill.parsed_data.billing_period_to || '',
        usage_amount: bill.parsed_data.usage_amount?.toString() || '',
        usage_unit: bill.parsed_data.usage_unit || '',
      });
    }
  }, [bill]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleUpdate();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleApprove();
      }
      if (e.key === 'Escape') {
        navigate(-1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [formData]);

  const handleUpdate = async () => {
    if (!billId) return;

    const parsedData = {
      ...formData,
      amount_due: parseFloat(formData.amount_due) || 0,
      usage_amount: parseFloat(formData.usage_amount) || 0,
    };

    try {
      await updateFields.mutateAsync({ billId: billId!, updates: parsedData });
      toast({
        title: 'Success',
        description: 'Bill data updated successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleApprove = async () => {
    if (!billId) return;

    try {
      await approveBill.mutateAsync(billId!);
      toast({
        title: 'Success',
        description: 'Bill approved successfully',
      });
      navigate(-1);
    } catch (error: any) {
      toast({
        title: 'Approval failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleReject = async () => {
    if (!billId) return;

    try {
      await rejectBill.mutateAsync({ billId: billId!, reason: 'Sent for re-review' });
      toast({
        title: 'Success',
        description: 'Bill rejected for re-review',
      });
      navigate(-1);
    } catch (error: any) {
      toast({
        title: 'Rejection failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <ModuleAccessGuard moduleId="bill_processing">
        <div className="container mx-auto py-6 flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </ModuleAccessGuard>
    );
  }

  if (!bill) {
    return (
      <ModuleAccessGuard moduleId="bill_processing">
        <div className="container mx-auto py-6">
          <div className="text-center">
            <p className="text-muted-foreground">Bill not found</p>
            <Button onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </ModuleAccessGuard>
    );
  }

  const confidence = bill.confidence_scores || {};

  return (
    <ModuleAccessGuard moduleId="bill_processing">
      <div className="container mx-auto py-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: PDF Preview (60% width on large screens) */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardContent className="p-4">
                <PDFPreview fileUrl={bill.file_url} />
              </CardContent>
            </Card>
          </div>

          {/* Right: Form (40% width on large screens) */}
          <div className="lg:col-span-1">
            <Card className="h-full overflow-y-auto">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Bill ID: {bill.id.slice(0, 8)}</CardTitle>
                  <Badge>{bill.status || 'parsed'}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Section 1: Invoice Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Invoice Information</h3>
                  <div className="space-y-2">
                    <Label>Invoice Number</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={formData.invoice_number}
                        onChange={(e) =>
                          setFormData({ ...formData, invoice_number: e.target.value })
                        }
                      />
                      {confidence.invoice_number && (
                        <ConfidenceBadge score={confidence.invoice_number} />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Invoice Date</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={formData.invoice_date}
                        onChange={(e) =>
                          setFormData({ ...formData, invoice_date: e.target.value })
                        }
                      />
                      {confidence.invoice_date && (
                        <ConfidenceBadge score={confidence.invoice_date} />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Amount Due</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount_due}
                        onChange={(e) =>
                          setFormData({ ...formData, amount_due: e.target.value })
                        }
                      />
                      {confidence.amount_due && (
                        <ConfidenceBadge score={confidence.amount_due} />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      />
                      {confidence.due_date && <ConfidenceBadge score={confidence.due_date} />}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Section 2: Billing Period */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Billing Period</h3>
                  <div className="space-y-2">
                    <Label>Period From</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={formData.billing_period_from}
                        onChange={(e) =>
                          setFormData({ ...formData, billing_period_from: e.target.value })
                        }
                      />
                      {confidence.billing_period_from && (
                        <ConfidenceBadge score={confidence.billing_period_from} />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Period To</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={formData.billing_period_to}
                        onChange={(e) =>
                          setFormData({ ...formData, billing_period_to: e.target.value })
                        }
                      />
                      {confidence.billing_period_to && (
                        <ConfidenceBadge score={confidence.billing_period_to} />
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Section 3: Usage */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Usage</h3>
                  <div className="space-y-2">
                    <Label>Usage Amount</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.usage_amount}
                        onChange={(e) =>
                          setFormData({ ...formData, usage_amount: e.target.value })
                        }
                      />
                      {confidence.usage_amount && (
                        <ConfidenceBadge score={confidence.usage_amount} />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Usage Unit</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={formData.usage_unit}
                        onChange={(e) =>
                          setFormData({ ...formData, usage_unit: e.target.value })
                        }
                      />
                      {confidence.usage_unit && (
                        <ConfidenceBadge score={confidence.usage_unit} />
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Section 4: Additional */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Additional Information</h3>
                  <div className="space-y-2">
                    <Label>Customer</Label>
                    <p className="text-sm">{bill.customer_name || 'N/A'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Supplier</Label>
                    <p className="text-sm">{bill.supplier_name || 'N/A'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Account Number</Label>
                    <p className="text-sm font-mono">{bill.account_number || 'N/A'}</p>
                  </div>
                </div>

                <Separator />

                {/* Action Buttons */}
                <div className="space-y-2">
                  <Button
                    onClick={handleUpdate}
                    disabled={updateFields.isPending}
                    className="w-full"
                  >
                    {updateFields.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      'Update Parser Data (Ctrl+S)'
                    )}
                  </Button>
                  <Button
                    onClick={handleApprove}
                    disabled={approveBill.isPending}
                    variant="default"
                    className="w-full"
                  >
                    {approveBill.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Approving...
                      </>
                    ) : (
                      'Approve Bill (Ctrl+Enter)'
                    )}
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={rejectBill.isPending}
                    variant="outline"
                    className="w-full"
                  >
                    {rejectBill.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Rejecting...
                      </>
                    ) : (
                      'Reject for Re-review'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ModuleAccessGuard>
  );
};

export default BillDetails;
